import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { NGProviderBrowser, formatSizeForProviderBrowser } from "../../../ngProviderBrowser";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { v4 as uuidv4 } from "uuid";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { store } from "../../../../../store";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { CpArrayItemTaint } from "../aws/CpArrayItemTaint";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { EllipsisOutlined } from "@ant-design/icons";
import { CpInputNumber } from "../../../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { NodePoolDetectedTags } from "../detectedTags";
import { NodePoolCoreButton } from "../nodePoolCoreButton";

interface Props {
  index: number;
}
export const TritonNodePool: React.FC<Props> = ({ index }) => {
  const { id: formId } = useCpFormContext();

  const name = useCpFormValue(`triton.nodePools.${index}.name`);
  const packageId = useCpFormValue(`triton.nodePools.${index}.packageId`);
  const publicNetworkId = useCpFormValue(`triton.nodePools.${index}.publicNetworkId`);
  const privateNetworkIds = useCpFormValue(`triton.nodePools.${index}.privateNetworkIds`);
  const overrideImageId = useCpFormValue(`triton.nodePools.${index}.overrideImageId`);

  const [browserKey, setBrowserKey] = React.useState("");

  return (
    <>
      <div className="cpln-form-section cpln-ant-tab-content">
        <Card>
          <CardTitle>
            Node Pool{name ? ` | ${name}` : ""}
            <CpErrorWarningArrayIcon names={[`triton.nodePools.${index}`]} />
          </CardTitle>
          <CardContent>
            <div>
              <NodePoolDetectedTags name={`triton.nodePools.${index}.labels`} />
              <CpFormItem>
                <CpLabel name={`triton.nodePools.${index}.name`}>Name</CpLabel>
                <CpInput name={`triton.nodePools.${index}.name`} style={{ width: 600 }} />
                <CpErrorWarning name={`triton.nodePools.${index}.name`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`triton.nodePools.${index}.packageId`}>Package ID</CpLabel>
                <Space.Compact>
                  <CpInput name={`triton.nodePools.${index}.packageId`} style={{ width: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("packages")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`triton.nodePools.${index}.packageId`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex gap-2">
                  <div className="flex flex-col">
                    <CpLabel name={`triton.nodePools.${index}.minSize`}>Min Size</CpLabel>
                    <CpInputNumber name={`triton.nodePools.${index}.minSize`} />
                  </div>
                  <div className="flex flex-col">
                    <CpLabel name={`triton.nodePools.${index}.maxSize`}>Max Size</CpLabel>
                    <CpInputNumber name={`triton.nodePools.${index}.maxSize`} />
                  </div>
                </div>
                <CpErrorWarningArray
                  names={[`triton.nodePools.${index}.minSize`, `triton.nodePools.${index}.maxSize`]}
                />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`triton.nodePools.${index}.overrideImageId`}>Override Image ID</CpLabel>
                <Space.Compact>
                  <CpInput name={`triton.nodePools.${index}.overrideImageId`} style={{ width: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("images")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`triton.nodePools.${index}.overrideImageId`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`triton.nodePools.${index}.publicNetworkId`}>Public Network ID</CpLabel>
                <Space.Compact>
                  <CpInput name={`triton.nodePools.${index}.publicNetworkId`} style={{ width: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("public-networks")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`triton.nodePools.${index}.publicNetworkId`} />
              </CpFormItem>
              <div style={{ maxWidth: 600 }}>
                <CpArrayString label={`Private Network IDs`} name={`triton.nodePools.${index}.privateNetworkIds`} />
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                  label={`Triton Tags`}
                  labels={["Name", "Value"]}
                  name={`triton.nodePools.${index}.tritonTags`}
                >
                  <CpArrayItemNameValuePair />
                </CpArrayObject>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Labels</CpLabel>
                    <NodePoolCoreButton name={`triton.nodePools.${index}.labels`} />
                  </div>
                  <div className="-mt-2">
                    <CpArrayObject
                      factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                      labels={["Name", "Value"]}
                      name={`triton.nodePools.${index}.labels`}
                    >
                      <CpArrayItemNameValuePair />
                    </CpArrayObject>
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), key: "", value: "", effect: "" })}
                  labels={["Key", "Value", "Effect"]}
                  label="Taints"
                  name={`triton.nodePools.${index}.taints`}
                >
                  <CpArrayItemTaint />
                </CpArrayObject>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {browserKey === "public-networks" ? (
        <NGProviderBrowser
          title="Browse Triton Public Networks"
          initialValue={publicNetworkId}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "public-networks",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(formId, `triton.nodePools.${index}.publicNetworkId`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          sortable
          labels={["Name", "Id", "Description", "Subnet", "VLAN Id"]}
          getData={(item: any) => [item.id, item.name, item.id, item.description, item.subnet, item.vlan_id]}
        />
      ) : null}
      {browserKey === "private-networks" ? (
        <NGProviderBrowser
          title="Browse Triton Private Networks"
          initialValue={privateNetworkIds.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "private-networks",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(_value) => {
            const values: string[] = _value as any;
            for (const value of values) {
              if (privateNetworkIds.some((i) => i.value === value)) {
                continue;
              }
              privateNetworkIds.push({ id: uuidv4(), value: value });
              formSetValue(formId, `triton.nodePools.${index}.privateNetworkIds`, privateNetworkIds);
            }
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          sortable
          multipleChoice
          labels={["Name", "Id", "Description", "Subnet", "VLAN Id"]}
          getData={(item: any) => [item.id, item.name, item.id, item.description, item.subnet, item.vlan_id]}
        />
      ) : null}
      {browserKey === "images" ? (
        <NGProviderBrowser
          title="Browse Triton Images"
          initialValue={overrideImageId}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "images",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(formId, `triton.nodePools.${index}.overrideImageId`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          sortable
          distinctFilters={["Type", "Brand"]}
          filteredColumns={["Name"]}
          labels={["Name", "Id", "Size", "Type", "Brand", "Version"]}
          getData={(item: any) => [
            item.id,
            item.name,
            item.id,
            [item.image_size || 0, item.image_size ? `${item.image_size / 1024}Gi` : "-"],
            item.type,
            item.requirements?.brand,
            item.version,
          ]}
        />
      ) : null}
      {browserKey === "packages" ? (
        <NGProviderBrowser
          title="Browse Triton Packages"
          initialValue={packageId}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "packages",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(formId, `triton.nodePools.${index}.packageId`, value as any);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          filteredColumns={["Name"]}
          distinctFilters={["Cores", "Memory", "Disk", "Brand"]}
          labels={["Name", "Cores", "Memory", "Disk", "Brand", "Version", "Id", "Description"]}
          sortable
          getData={(item: any) => {
            return [
              item.id,
              item.name,
              item.vcpus,
              [item.memory, formatSizeForProviderBrowser(item.memory, "MB")],
              [item.disk, formatSizeForProviderBrowser(item.disk, "MB")],
              item.brand,
              item.version,
              item.id,
              item.description,
            ];
          }}
        />
      ) : null}
    </>
  );
};
